import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, Trash2, ChevronDown, ChevronUp } from "lucide-react";

const STATUS_COLORS = {
  new: "bg-brand-red text-brand-ink",
  contacted: "bg-brand-pink text-brand-ink",
  closed: "bg-brand-ink text-white",
};

export default function AdminQuotes() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/admin/quotes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/quotes/${id}`, { status });
    toast.success("Status updated");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    await api.delete(`/admin/quotes/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-quotes-page">
      <div className="eyebrow">Inbox</div>
      <h1 className="font-display text-4xl text-brand-ink mt-2">Quote requests</h1>

      <div className="mt-8 space-y-3">
        {items.length === 0 && <div className="text-brand-stone text-sm">No quote requests yet.</div>}
        {items.map((q) => (
          <div key={q.id} data-testid={`quote-row-${q.id}`} className="bg-white border border-brand-line rounded-2xl">
            <div className="p-5 flex items-center gap-4 flex-wrap">
              <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold ${STATUS_COLORS[q.status] || "bg-brand-line"}`}>{q.status}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-brand-ink">{q.company_name}</div>
                <div className="text-xs text-brand-stone">{q.contact_name} · {new Date(q.created_at).toLocaleDateString()}</div>
              </div>
              <div className="text-xs text-brand-stone hidden md:flex items-center gap-4">
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {q.email}</span>
                {q.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {q.phone}</span>}
              </div>
              <select
                value={q.status}
                onChange={(e) => setStatus(q.id, e.target.value)}
                data-testid={`status-${q.id}`}
                className="h-9 rounded-full border border-brand-line px-3 text-xs bg-white"
              >
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="closed">Closed</option>
              </select>
              <button onClick={() => setExpanded(expanded === q.id ? null : q.id)} data-testid={`toggle-${q.id}`} className="w-9 h-9 grid place-items-center rounded-full border border-brand-line">
                {expanded === q.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <button onClick={() => remove(q.id)} data-testid={`delete-quote-${q.id}`} className="w-9 h-9 grid place-items-center rounded-full border border-brand-line hover:bg-red-50 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
            </div>

            {expanded === q.id && (
              <div className="border-t border-brand-line p-5 grid md:grid-cols-2 gap-6" data-testid={`quote-details-${q.id}`}>
                <div className="space-y-2 text-sm">
                  <div><span className="text-brand-stone">Business type:</span> {q.business_type || "—"}</div>
                  <div><span className="text-brand-stone">Location:</span> {q.city || "—"}{q.state ? `, ${q.state}` : ""}</div>
                  <div><span className="text-brand-stone">Volume:</span> {q.estimated_volume || "—"}</div>
                  <div><span className="text-brand-stone">Email:</span> {q.email}</div>
                  <div><span className="text-brand-stone">Phone:</span> {q.phone || "—"}</div>
                  {q.notes && <div className="pt-2"><div className="text-brand-stone mb-1">Notes:</div><div className="bg-white border border-brand-line rounded-xl p-3 whitespace-pre-line">{q.notes}</div></div>}
                </div>
                <div>
                  <div className="text-brand-stone text-sm mb-2">Items ({q.items.length})</div>
                  <ul className="space-y-2">
                    {q.items.map((i, k) => (
                      <li key={`${i.product_id}-${i.size_label}-${k}`} className="bg-white border border-brand-line rounded-xl px-3 py-2 text-sm flex items-center justify-between">
                        <span>{i.product_name} <span className="text-brand-stone">· {i.size_label}</span></span>
                        <span className="font-semibold">×{i.quantity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
